import defaults from 'lodash/defaults'
import { POPOVER_TYPES } from '../vd-popover/vd-popover.service'

const DEFAULT_OPTIONS = {
  direction: 'center',
  inheritWidthFromTrigger: false,
  toggleOnTriggerClick: false,
  type: POPOVER_TYPES.TOOLTIP,
}

/**
 * Service for opening and closing tooltips.
 *
 * @class TooltipService
 */
export default class TooltipService {
  constructor($q, vdPopover) {
    'ngInject'
    this._$q = $q
    this._vdPopover = vdPopover
  }

  // ============================== Public API =============================== //

  /**
   * Open a tooltip with the given content. Only one tooltip or popover can be open at a time so if another tooltip or
   * popover is already open it will be closed before this one is opened.
   *
   * vdTooltip.open({ trigger, tooltipText: 'Hello World!' })
   *
   * vdTooltip.open({ trigger, template: '<vd-tooltip>Hello <strong>World</strong>!</vd-tooltip>' })
   *
   * @method open
   *
   * @param  {Object} options
   *         All the same options supported by the Modal service and vdPopover service are supported (template,
   *         templateUrl, controller, constraints, etc).
   *
   *         @param {String|jQuery|HTMLElement} trigger
   *                The element that triggered the tooltip - used to align the tooltip and beak
   *
   *         @param {String} [tooltipText]
   *                The plain text content to display inside the tooltip. If you wish to render richer content inside
   *                the tooltip then instead supply a `template` or `tempateUrl` that wraps the content in a
   *                `vd-tooltip` element (e.g. { template: '<vd-tooltip>Hello <b>World</b>!</vd-tooltip>' })
   *
   * @return {Promise<Tooltip>} A promise resolved with the tooltip instance once the tooltip is open. The tooltip
   *         instance object extends the angular-modal-service instance and so provides `close`, `closed` promises etc.
   */
  open(options) {
    // Ensure we don't close an open popover that is not a tooltip
    if (
      this._vdPopover.isOpen({ match: ':not(.vd-popover-container--tooltip)' })
    ) {
      return this._$q.reject()
    }

    const config = defaults(options, DEFAULT_OPTIONS)

    if (options.tooltipText) {
      config.template = '<vd-tooltip>{{$ctrl.options.tooltipText}}</vd-tooltip>'
    }

    return this._vdPopover.open(config)
  }

  /**
   * Close the tooltip if there is one open.
   *
   * @method close
   *
   * @return {Promise} A promise resolved once the tooltip has been closed
   */
  close() {
    return this._vdPopover.close({
      // Ensure we only close the open popover if it is actually a tooltip
      match: '.vd-popover-container--tooltip',
    })
  }
}
